import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../api/client";
import AuthShell from "../components/AuthShell/AuthShell";
import { Field } from "../components/ui";
import "./auth.css";

/** Mirrors the server rules so the user sees them before submitting. */
function localIssues({ email, username, password }) {
  const issues = {};
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    issues.email = "Enter a valid email address.";
  }
  if (username && !/^[a-zA-Z0-9_.-]{3,24}$/.test(username)) {
    issues.username = "3-24 characters: letters, numbers, dot, dash, underscore.";
  }
  if (password) {
    if (password.length < 10) issues.password = "Use at least 10 characters.";
    else if (!/[a-z]/i.test(password)) issues.password = "Include at least one letter.";
    else if (!/[0-9]/.test(password)) issues.password = "Include at least one number.";
  }
  return issues;
}

export default function RegisterPage() {
  const { register } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    referralCode: params.get("ref") || "",
  });
  const [referrer, setReferrer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  const issues = localIssues(form);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Confirms the invite is real before signup, so the user knows the bonus
  // will actually apply rather than finding out afterwards.
  useEffect(() => {
    const code = form.referralCode.trim();
    if (!code) {
      setReferrer(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get(`/api/auth/referral/${encodeURIComponent(code)}`)
        .then((r) => !cancelled && setReferrer(r))
        .catch(() => !cancelled && setReferrer(null));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.referralCode]);

  async function onSubmit(event) {
    event.preventDefault();
    setServerError("");
    setBusy(true);
    try {
      await register({
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        referralCode: form.referralCode.trim() || undefined,
      });
      toast.ok("Account created");
      navigate("/pricing", { replace: true });
    } catch (err) {
      setServerError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const complete =
    form.email && form.username && form.password && Object.keys(issues).length === 0;

  return (
    <AuthShell
      eyebrow="Sign up"
      title="Create an account"
      sub="Accounts are free. A plan or top-up credits are needed before you can generate."
      footer={
        <>
          Already registered? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
          <Field label="Email" id="email" required error={issues.email}>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              required
            />
          </Field>

          <Field label="Username" id="username" required error={issues.username}>
            <input
              id="username"
              type="text"
              autoComplete="username"
              value={form.username}
              onChange={set("username")}
              placeholder="yourhandle"
              required
            />
          </Field>

          <Field
            label="Password"
            id="password"
            required
            error={issues.password}
            hint="At least 10 characters, with a letter and a number."
          >
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set("password")}
              required
            />
          </Field>

          <Field
            label="Referral code"
            id="ref"
            hint={
              referrer?.valid
                ? `Invited by ${referrer.username} — you both get a bonus when you buy a plan.`
                : "Optional."
            }
            error={
              form.referralCode && referrer && !referrer.valid
                ? "That code does not match anyone. You can still sign up."
                : ""
            }
          >
            <input
              id="ref"
              type="text"
              value={form.referralCode}
              onChange={set("referralCode")}
              placeholder="ABC12345"
            />
          </Field>

          {serverError && (
            <p className="auth-error" role="alert">
              {serverError}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={busy || !complete}
          >
            {busy && <span className="spinner" aria-hidden="true" />}
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>

      <p className="auth-disclaimer">
        Fictional data only. Not a government-issued document.
      </p>
    </AuthShell>
  );
}
